/** Read-only list calls on routes that exist on `main`. Shapes copied from the route files (workers/api/src). */
import type { ApiClient } from "./client";

export interface Payment {
  id: string; billId: string; memberId: string; branchId: string;
  method: "cash" | "member_credit" | "upi_qr" | "payment_link" | string;
  /** Null without `revenue.bill_amounts`. */
  amount: number | null; currency: string; status: string; businessDate: string; createdAt: number;
}
/** One entry of `GET /home/collections`: money that came in (the server keeps captured, non-wallet payments only). */
export interface Collection { amount: number; method: "cash" | "upi_qr" | "payment_link" | string; createdAt: number }
/** Bill money figures are null without `revenue.bill_amounts` (server redaction, D57). */
export interface BillSummary {
  id: string; memberId: string; branchId: string; currency: string; status: string; total: number | null; paidTotal: number | null; balance: number | null;
  paymentStatus: "unpaid" | "partial" | "paid" | "overpaid"; buyerName: string | null; businessDate: string; dueDate: string | null; createdAt: number; voidedAt: number | null;
}
export interface CheckinRow { id: string; memberId: string; memberName: string | null; branchId: string; scannedAt: number; businessDate: string; decision: string; countedAsVisit: boolean }
export interface Occurrence {
  id: string; name: string; trainerId: string | null; branchId: string; date: string; start: string; end: string; capacity: number; status: string;
  seatsTaken?: number; seatsLeft?: number; waitlisted?: number;
}
export interface MemberHit { id: string; name: string; phone: string | null; status: string }
export interface LeadHit { id: string; name: string | null; phone: string | null }

export const PAGE = 200;
type Page<K extends string, T> = { [P in K]: T[] } & { nextCursor: string | null };

export const fetchPayments = (c: ApiClient, q: { from: string; to: string; branchId?: string }) =>
  c.get<Page<"payments", Payment>>("/payments", { ...q, limit: PAGE });
/** Home's collected figure: its own lever, `revenue.home_collections`. `capped` means the day had more than the server reads. */
export const fetchCollections = (c: ApiClient, q: { date: string; branchId?: string }) =>
  c.get<{ date: string; currency: string; collections: Collection[]; capped: boolean }>("/home/collections", q);
export const fetchBills = (c: ApiClient, q: { from?: string; to?: string; branchId?: string; paymentStatus?: string }) =>
  c.get<Page<"bills", BillSummary>>("/bills", { ...q, limit: PAGE });
export const fetchCheckins = (c: ApiClient, q: { date: string; branchId?: string }) =>
  c.get<Page<"checkins", CheckinRow>>("/checkins", { ...q, limit: PAGE });
export const fetchOccurrences = (c: ApiClient, q: { from: string; to: string; branchId?: string }) =>
  c.get<{ occurrences: Occurrence[] }>("/classes/occurrences", q);
export const searchMembers = (c: ApiClient, q: string, signal?: AbortSignal) =>
  c.get<Page<"members", MemberHit>>("/members", { q, limit: 8 }, signal).then((r) => r.members);
export const searchLeads = (c: ApiClient, q: string, signal?: AbortSignal) =>
  c.get<Page<"leads", LeadHit>>("/leads", { q }, signal).then((r) => r.leads.slice(0, 8));
